import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ClientImplementation,
  KanbanColumn,
  PHASE_COLORS,
  PHASE_LABELS,
  STATUS_LABELS,
  ImplementationPhase,
} from '@/types/customerSupport';
import { Building2, Calendar } from 'lucide-react';

interface ImplementationCardProps {
  implementation: ClientImplementation;
  columnsByKey?: Record<string, KanbanColumn>;
  onClick: () => void;
  onViewObservations?: () => void;
}

const ImplementationCard: React.FC<ImplementationCardProps> = ({
  implementation,
  columnsByKey,
  onClick,
  onViewObservations,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: implementation.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const diasDesdeCriacao = Math.floor(
    (new Date().getTime() - new Date(implementation.created_at).getTime()) / (1000 * 60 * 60 * 24)
  );

  const client = implementation.client;
  const phaseKey = implementation.current_phase as string;
  const dynamicCol = columnsByKey?.[phaseKey];
  const phaseColor =
    dynamicCol?.color ?? PHASE_COLORS[phaseKey as ImplementationPhase] ?? '#94a3b8';
  const phaseLabel =
    dynamicCol?.label ?? PHASE_LABELS[phaseKey as ImplementationPhase] ?? phaseKey;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card
        className="cursor-pointer hover:shadow-md transition-shadow bg-white"
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="space-y-3">
            <div>
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-sm line-clamp-1">
                  {client?.legal_name || 'Cliente não encontrado'}
                </h4>
                {implementation.status === 'BLOCKED' && (
                  <Badge variant="destructive" className="text-xs">
                    Bloqueado
                  </Badge>
                )}
              </div>
              {client?.trade_name && (
                <p className="text-xs text-muted-foreground mt-1">
                  {client.trade_name}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: phaseColor }}
              />
              <span className="text-xs font-medium" style={{ color: phaseColor }}>
                {phaseLabel}
              </span>
            </div>

            <div className="space-y-1.5">
              {client?.company_tax_id && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Building2 className="h-3 w-3 flex-shrink-0" />
                  <span>CNPJ: {client.company_tax_id}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3 flex-shrink-0" />
                <span>{diasDesdeCriacao} dias</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <Badge variant="outline" className="text-xs">
                {STATUS_LABELS[implementation.status]}
              </Badge>

              {implementation.observations && implementation.observations.length > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs cursor-pointer hover:bg-secondary/80"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewObservations?.();
                  }}
                >
                  {implementation.observations.length} obs.
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImplementationCard;
